'use client';

import Image from 'next/image';
import { Wallet } from 'lucide-react';
import { useState } from 'react';
import { cn, currencyFormatter } from '@/lib/utils';
import { useHoldingPositions } from '@/hooks/use-holding-positions';
import type {
  HoldingPositionItem,
  HoldingPositionStatus,
  HoldingPositionsSummary,
} from '@/hooks/use-holding-positions';
import { PolymarketMarket } from './types';
import MarketStatus from '../../cards/MarketStatus';
import { useTranslation } from '@/lib/i18n';
import { ChangeText } from '../../cards/ChangeText';

const DEFAULT_VISIBLE_POSITIONS = 6;

interface HoldingPositionsProps {
  eventId: number;
  markets: PolymarketMarket[];
  onAskPythra?: () => void;
  className?: string;
  isClosed: boolean;
}

export default function HoldingPositions({
  eventId,
  markets,
  onAskPythra,
  className,
  isClosed,
}: HoldingPositionsProps) {
  const { t } = useTranslation();
  const holdings = useHoldingPositions({ eventId, markets });
  const isUsingHoldingData = holdings.walletSummaries.length > 0;

  if (!isUsingHoldingData) {
    return null;
  }

  return (
    <section className={className}>
      <h2 className="text-lg font-bold leading-5 py-5 text-white">{t('My Positions')}</h2>
      <div className="space-y-3 relative">
        {holdings.walletSummaries.map((summary, index) => (
          <WalletPositionCard
            key={summary.wallet}
            summary={summary}
            isClosed={isClosed}
            status={summary.status}
            onAskPythra={index === 0 ? onAskPythra : undefined}
          />
        ))}
      </div>
    </section>
  );
}

function WalletPositionCard({
  summary,
  status,
  isClosed,
  onAskPythra,
}: {
  summary: HoldingPositionsSummary;
  status?: HoldingPositionStatus;
  onAskPythra?: () => void;
  isClosed: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasHiddenPositions = summary.positions.length > DEFAULT_VISIBLE_POSITIONS;
  const visiblePositions = expanded
    ? summary.positions
    : summary.positions.slice(0, DEFAULT_VISIBLE_POSITIONS);

  return (
    <div className="@container rounded-xl border border-[#242424] bg-[#151515] px-6 py-5">
      <div className="flex items-center gap-4">
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-medium leading-7 text-white">
            {currencyFormatter(summary.totalValue, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
          </span>
          <ChangeText value={summary.totalPnlPercent} digits={2} showTriangle={false} />
        </div>

        <div className="h-5 w-px bg-[#343434]" />

        <div className="flex items-center gap-2 text-sm text-[#757575]">
          <Wallet size={16} strokeWidth={2} />
          <span>{summary.walletAddress}</span>
        </div>

        {isClosed && <MarketStatus className="" />}

        {/* {status && <ResolutionStatusBadge status={status} />} */}

        {onAskPythra && (
          <button
            type="button"
            onClick={onAskPythra}
            className="ml-auto h-9 rounded-full overflow-hidden p-px text-sm font-medium text-white"
            style={{
              background:
                'linear-gradient(89.96deg, #FF5000 0.03%, #FFBE49 23.46%, #FFF6A6 41.54%, #A269FF 57.3%, #2C6AF9 78.6%, #3DE0A3 100%)',
            }}
          >
            <div className="flex h-full items-center gap-2 bg-black px-4 rounded-full">
              <Image src="/logo.svg" alt="" width={16} height={16} />
              {t('Ask Pythra')}
            </div>
          </button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 @max-2xl:grid-cols-2">
        {visiblePositions.map((position) => (
          <PositionChip key={position.id} position={position} />
        ))}
      </div>

      {hasHiddenPositions && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className="h-8 rounded-full border border-[#303030] px-4 text-sm font-medium text-[#B7B9BC] transition-colors hover:border-[#4A4A4A] hover:text-white"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? t('Show Less') : t('Show More')}
          </button>
        </div>
      )}
    </div>
  );
}

function ResolutionStatusBadge({ status }: { status: HoldingPositionStatus }) {
  const isResolved = status === 'resolved';

  return (
    <span
      className={cn(
        'inline-flex h-5 items-center rounded-full px-2 text-xs font-medium capitalize',
        isResolved ? 'bg-[#073D2C] text-[#00E59B]' : 'bg-[#3A2A10] text-[#FFBE49]',
      )}
    >
      {status}
    </span>
  );
}

function PositionChip({ position }: { position: HoldingPositionItem }) {
  const isYes = position.outcome.toUpperCase() === 'YES';
  const isZeroValue = position.currentValue === 0;
  const displayValue = position.currentValue; // isZeroValue ? position.cashPnl : position.currentValue;
  const valueClassName = isZeroValue ? 'text-[#B7B9BC]' : 'text-[#00E59B]';

  return (
    <div className="flex h-9 items-center rounded-lg border border-[#303030] bg-[#0D0D0D] px-2.5">
      <span
        className={cn(
          'flex h-4.5 min-w-10 items-center justify-center rounded-sm px-2 text-xs font-medium',
          isYes ? 'bg-[#073D2C] text-[#00E59B]' : 'bg-[#4A1111] text-[#FF4242]',
        )}
      >
        {position.outcome.toUpperCase()}
      </span>
      <span className="ml-2 text-sm font-medium text-white">{position.label}</span>
      <span className={cn('ml-auto text-sm', valueClassName)}>
        {currencyFormatter(displayValue, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })}
      </span>
    </div>
  );
}
